import React from 'react';
import { CheckCircle, AlertTriangle, Wrench, Clock, Shield, Phone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { siteInfo } from '../data/mock';

const Installation = () => {
  const installationSteps = [
    "Déballer les colis de manière ordonnée et rassembler les pièces par groupes",
    "Se familiariser avec le vocabulaire utilisé et le nom des différentes pièces",
    "Visualiser la mise en place des pièces et schématiser le montage",
    "Repérer les points problématiques avant de commencer"
  ];

  const precautions = [
    "⚠️ IMPORTANT : Le système d'humidification (contrôle du taux d'hygrométrie) est exceptionnel et UNIQUEMENT utilisé pour les applications agricoles, viticoles et de pulvérisation (épuration de l'air). Pour les voitures, motos, camions et utilitaires, seul le kit principal suffit.",
    "Installation aisée en 50 minutes (sans modification moteur) avec test routier",
    "Ne jamais forcer sur les tubes en inox, ils se cintrent et ne se plient pas",
    "Bien serrer tous les colliers de serrage",
    "Vérifier l'étanchéité des connexions avant mise en service",
    "Fixer correctement les flexibles inox",
    "Éviter les frottements métal contre métal si l'aide des colliers inox et des isolants en mousse",
    "Terminaison du circuit avant le filtre à Air",
    "Vérifier l'étanchéité du circuit"
  ];

  const requiredTools = [
    "Coupe tube ou meuleuse",
    "Perceuse avec fraise d'étagement ou foret de 15/16mm",
    "Clé de 24 ou Clé à molette",
    "Pince multi-prise"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Wrench className="mx-auto mb-6" size={64} />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Installation du Kit Ecopra
            </h1>
            <p className="text-xl md:text-2xl mb-4">
              Notice de montage - Kit Buveur d'Air
            </p>
            <div className="flex items-center justify-center gap-6 text-lg">
              <div className="flex items-center gap-2">
                <Clock size={24} />
                <span>Montage: 30-45 min</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield size={24} />
                <span>Installation professionnelle disponible</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Alert className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-400">
              <AlertTriangle className="text-red-600" />
              <AlertDescription className="text-gray-900">
                <h3 className="font-bold text-xl mb-3 text-red-700">⚠️ LIRE ATTENTIVEMENT</h3>
                <p className="mb-3">
                  Avant de vous lancer dans l'installation, il est recommandé de déballer les colis de manière 
                  ordonnée et de rassembler les pièces par groupes, comme vous les trouverez dans le tableau 
                  d'inventaire.
                </p>
                <p className="mb-3">
                  L'intérêt de procéder à cet inventaire ordonné est de se familiariser avec le vocabulaire utilisé, 
                  le nom des différentes pièces, afin de maîtriser la lecture de cette notice.
                </p>
                <p>
                  Cette lecture vous permettra d'avoir une vue globale des différentes phases que vous allez 
                  devoir suivre, de repérer les points éventuellement problématiques, et vous pourrez anticiper 
                  les difficultés que vous pourriez devoir résoudre pour aller au bout du travail.
                </p>
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </section>

      {/* Tools Required */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
                Outils à Prévoir
              </span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {requiredTools.map((tool, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#7CB342] to-[#00BCD4] rounded-full flex items-center justify-center">
                        <Wrench className="text-white" size={24} />
                      </div>
                      <p className="font-semibold text-gray-900">{tool}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Installation Steps */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
                Étapes d'Installation
              </span>
            </h2>

            <div className="space-y-6">
              {installationSteps.map((step, index) => (
                <Card key={index} className="border-l-4 border-[#00BCD4]">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#7CB342] to-[#00BCD4] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-xl">{index + 1}</span>
                      </div>
                      <p className="text-gray-700 pt-2">{step}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Installation Schema */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
                Schéma d'Installation
              </span>
            </h2>

            <Card className="overflow-hidden border-2 border-[#00BCD4]">
              <CardContent className="p-0">
                <img
                  src="https://customer-assets.emergentagent.com/job_news-3/artifacts/vnc3l1mb_WhatsApp%20Image%202026-04-16%20at%2009.09.29.jpeg"
                  alt="Schéma d'installation technique Ecopra"
                  className="w-full h-auto"
                />
                <div className="p-6 bg-gradient-to-r from-[#7CB342]/10 to-[#00BCD4]/10">
                  <h3 className="font-bold text-xl text-gray-800 mb-4">Légende du schéma :</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="flex items-start gap-3">
                      <div className="bg-yellow-400 text-black font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">1</div>
                      <div>
                        <p className="font-semibold text-gray-800">Prise d'air principale</p>
                        <p className="text-sm text-gray-600">Entrée d'air depuis la calandre</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-yellow-400 text-black font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">2</div>
                      <div>
                        <p className="font-semibold text-gray-800">Réacteur Ecopra</p>
                        <p className="text-sm text-gray-600">Traitement de l'air</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-yellow-400 text-black font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">3</div>
                      <div>
                        <p className="font-semibold text-gray-800">Système d'hygrométrie</p>
                        <p className="text-sm text-gray-600">Contrôle du taux d'humidité (agricole/viticole uniquement)</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-yellow-400 text-black font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">4</div>
                      <div>
                        <p className="font-semibold text-gray-800">Filtre à air d'origine</p>
                        <p className="text-sm text-gray-600">Filtration finale avant admission</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-yellow-400 text-black font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">5</div>
                      <div>
                        <p className="font-semibold text-gray-800">Connexions et serflex</p>
                        <p className="text-sm text-gray-600">Fixations des durites inox</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 bg-pink-50 p-3 rounded-lg">
                      <div className="w-12 h-1 bg-pink-500"></div>
                      <span className="font-semibold text-gray-800">DURITE INOX</span>
                    </div>
                    <div className="flex items-center gap-3 bg-blue-50 p-3 rounded-lg">
                      <div className="flex items-center gap-1">
                        <div className="w-8 h-1 bg-gradient-to-r from-blue-500 to-green-500"></div>
                        <span className="text-xl">→</span>
                      </div>
                      <span className="font-semibold text-gray-800">SENS CIRCULATION GAZ</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Installation Photos */}
            <div className="mt-8 space-y-6">
              <Card className="overflow-hidden border-2 border-[#7CB342]">
                <CardContent className="p-0">
                  <img
                    src="https://customer-assets.emergentagent.com/job_news-3/artifacts/gkp3ap9n_WhatsApp%20Image%202026-04-16%20at%2009.10.48.jpeg"
                    alt="Étape 3 et 4 - Insertion du flexible et filtre à air"
                    className="w-full h-auto"
                  />
                  <div className="p-4 bg-gradient-to-r from-[#7CB342]/10 to-[#00BCD4]/10">
                    <p className="text-sm text-gray-700 font-semibold">
                      <span className="inline-block bg-yellow-400 text-black rounded-full w-6 h-6 text-center mr-2">3</span>
                      Insertion du flexible de sortie dans l'admission d'air du véhicule
                    </p>
                    <p className="text-sm text-gray-700 font-semibold mt-2">
                      <span className="inline-block bg-yellow-400 text-black rounded-full w-6 h-6 text-center mr-2">4</span>
                      Filtre à air - Schéma du flux d'air
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="overflow-hidden border-2 border-red-400">
                <CardContent className="p-0">
                  <img
                    src="https://customer-assets.emergentagent.com/job_news-3/artifacts/9rmwxm76_WhatsApp%20Image%202026-04-16%20at%2009.12.18.jpeg"
                    alt="Instructions détaillées étape 3 et schéma filtre à air"
                    className="w-full h-auto"
                  />
                  <div className="p-4 bg-red-50">
                    <p className="text-sm text-red-700 font-bold">
                      ⚠️ IMPORTANT : Vous devez percer la conduite d'admission AVANT le filtre à air
                    </p>
                    <p className="text-sm text-gray-700 mt-2">
                      Le flexible inox doit être inséré de 10 à 20 cm à l'intérieur de la durite d'admission, 
                      parallèle au flux d'air entrant.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-gradient-to-br from-[#7CB342]/10 to-[#00BCD4]/10">
                <CardHeader>
                  <CardTitle className="text-lg">Composants principaux</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-[#7CB342] flex-shrink-0 mt-1" size={16} />
                      <span>Durite d'Air primaire en provenance de calandre</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-[#7CB342] flex-shrink-0 mt-1" size={16} />
                      <span>Réacteur</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-[#7CB342] flex-shrink-0 mt-1" size={16} />
                      <span>Système hygrométrie</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-[#7CB342] flex-shrink-0 mt-1" size={16} />
                      <span>Durites thermolinox</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-[#7CB342] flex-shrink-0 mt-1" size={16} />
                      <span>4 connexions écrous segments</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-[#7CB342]/10 to-[#00BCD4]/10">
                <CardHeader>
                  <CardTitle className="text-lg">Installation</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 mb-4">
                    <strong>L'installation ne touche pas au moteur.</strong>
                  </p>
                  <p className="text-sm text-gray-700">
                    La modification se fait uniquement au niveau du filtre à air. C'est la matière "air" 
                    qui est modifiée avant tous les éléments du moteur.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Precautions */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
                Précautions d'Utilisation
              </span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {precautions.map((precaution, index) => (
                <Card key={index} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                      <p className="text-sm text-gray-700">{precaution}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Real Installation Photo */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
                Exemple d'Installation Réelle
              </span>
            </h2>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img
                  src="https://customer-assets.emergentagent.com/job_news-3/artifacts/3ugj0zy9_Photo%20Ecopra1.jpg"
                  alt="Installation réelle du kit Ecopra dans un moteur"
                  className="w-full h-auto"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Professional Installation CTA */}
      <section className="py-16 bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Installation Professionnelle en Suisse
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Vous préférez confier l'installation à un professionnel ? Nous proposons un service 
            d'installation partout en Suisse.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/commander"
              className="inline-block bg-white text-[#00BCD4] font-bold px-10 py-4 rounded-full hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl"
            >
              Commander avec installation
            </a>
            <a
              href={`tel:${siteInfo.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white font-bold px-10 py-4 rounded-full hover:bg-white/10 transition-all"
            >
              <Phone size={20} />
              Appelez-nous : {siteInfo.phoneDisplay}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Installation;
